import React from 'react';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { cn, type ThemeClasses } from '@/ui/theme/ThemeContext';

/* ============================================================================
 * TYPE DEFINITIONS
 * ============================================================================ */

export interface ProgressSectionProps {
  progress: number;
  tempProgress: number | null;
  isDraggingProgress: boolean;
  onProgressStart: () => void;
  onProgressChange: (value: number) => void;
  onProgressEnd: () => void;
  theme: ThemeClasses;
}

export interface MilestoneProgressProps {
  progress: number;
  onProgressChange: (value: number) => void;
  theme: ThemeClasses;
}

/* ============================================================================
 * PROGRESS SECTION COMPONENT
 * Component for task progress controls
 * ============================================================================ */

export const ProgressSection: React.FC<ProgressSectionProps> = ({
  progress,
  tempProgress,
  isDraggingProgress,
  onProgressStart,
  onProgressChange,
  onProgressEnd,
  theme
}) => {
  const { t } = useSettings();
  const displayProgress = tempProgress !== null ? tempProgress : (progress || 0);
  
  return (
    <div className="space-y-2">
      <label className={cn("text-sm font-medium", theme.textSecondary)}>{t('Progress')}</label>
      <div className="flex items-center gap-4">
        <input 
          type="range" 
          min="0" 
          max="100" 
          value={displayProgress}
          aria-label={t('Progress')}
          onMouseDown={onProgressStart}
          onChange={(e) => onProgressChange(parseInt(e.target.value))} 
          onMouseUp={onProgressEnd}
          onTouchStart={onProgressStart}
          onTouchEnd={onProgressEnd}
          onBlur={() => {
            if (isDraggingProgress && tempProgress !== null) {
              onProgressEnd();
            }
          }}
          className={cn(
            "flex-1 cursor-pointer",
            "appearance-none bg-gray-200 dark:bg-gray-700 rounded-lg h-2",
            "[&::-webkit-slider-thumb]:appearance-none",
            "[&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4",
            "[&::-webkit-slider-thumb]:bg-zinc-700 dark:[&::-webkit-slider-thumb]:bg-zinc-300 [&::-webkit-slider-thumb]:rounded-full",
            "[&::-webkit-slider-thumb]:cursor-pointer",
            "[&::-webkit-slider-thumb]:shadow-md",
            "[&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4",
            "[&::-moz-range-thumb]:bg-zinc-700 dark:[&::-moz-range-thumb]:bg-zinc-300 [&::-moz-range-thumb]:rounded-full",
            "[&::-moz-range-thumb]:cursor-pointer",
            "[&::-moz-range-thumb]:border-0",
            "[&::-moz-range-thumb]:shadow-md"
          )}
        />
        <div className="flex items-center gap-2">
          <input 
            type="number" 
            min="0" 
            max="100" 
            value={displayProgress}
            aria-label={t('Progress') + ' (%)'}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (!isNaN(value) && value >= 0 && value <= 100) {
                onProgressChange(value);
              }
            }}
            className={cn(
              "w-16 text-center p-1 rounded border",
              theme.bgSecondary,
              theme.borderPrimary,
              theme.focusRing
            )}
          />
          <span className={theme.textSecondary}>%</span>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
 * MILESTONE PROGRESS COMPONENT
 * Checkbox component for milestone completion
 * ============================================================================ */

export const MilestoneProgress: React.FC<MilestoneProgressProps> = ({
  progress,
  onProgressChange,
  theme
}) => {
  const { t } = useSettings();
  return (
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        id="milestone-complete"
        checked={progress === 100}
        onChange={(e) => onProgressChange(e.target.checked ? 100 : 0)}
        className={cn(
          "h-5 w-5 rounded cursor-pointer",
          theme.focusRing
        )}
      />
      <label 
        htmlFor="milestone-complete" 
        className={cn("text-sm cursor-pointer", theme.textSecondary)}
      >
        {t('Mark as Complete')}
      </label>
    </div>
  );
};
